import { useEffect, useState } from "react";
import { ClientLink, RenewalsLink } from "@/components/EntityLinks";
import { Link } from "react-router-dom";
import { workApi } from "@/api/endpoints";
import type { MyBookResponse } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatCurrency, formatDate } from "@/utils/format";

export function MyBookPage() {
  const { user } = useAuth();
  const canViewClaims = canAccess(user, "claims.view");
  const [data, setData] = useState<MyBookResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    workApi
      .myBook()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load My Book"));
  }, []);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) {
    return (
      <div className="loading-screen" style={{ minHeight: "12rem" }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="My Book"
        subtitle="Your clients, renewals, open claims, and work items."
      />
      <section className="stat-grid">
        <StatCard label="Clients" value={data.customer_count} />
        <StatCard label="Active policies" value={data.active_policies} />
        <StatCard label="Open leads" value={data.open_leads} />
        <StatCard label="Renewals (90d)" value={data.renewals_due_90_days} />
        <StatCard label="Open claims" value={data.open_claims_count} />
        <StatCard label="Work items" value={data.open_work_items} />
      </section>

      <div className="detail-grid">
        <div className="card" style={{ padding: "1rem" }}>
          <h3>Clients</h3>
          {data.clients.length === 0 ? (
            <p className="empty-state">
              No clients in your book yet. Run{" "}
              <code>docker compose exec api python scripts/seed_demo_agency.py</code> for demo data.
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Claims</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <Link to={`/clients/${client.id}`}>{client.display_name}</Link>
                    </td>
                    <td>{client.open_claims}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" style={{ padding: "1rem" }}>
          <h3>
            Renewals (90 days) — <RenewalsLink>Renewal center</RenewalsLink>
          </h3>
          {data.renewals.length === 0 ? (
            <p className="empty-state">No renewals due in the next 90 days.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Policy</th>
                  <th>Client</th>
                  <th>Expires</th>
                  <th>Premium</th>
                </tr>
              </thead>
              <tbody>
                {data.renewals.map((renewal) => (
                  <tr key={renewal.policy_id}>
                    <td>
                      <Link to={`/policies/${renewal.policy_id}`}>{renewal.policy_number}</Link>
                    </td>
                    <td>
                      {renewal.customer_id ? (
                        <Link to={`/clients/${renewal.customer_id}`}>{renewal.customer_name}</Link>
                      ) : (
                        renewal.customer_name ?? "—"
                      )}
                    </td>
                    <td>
                      {renewal.days_until_renewal}d
                      {renewal.expiration_date ? ` · ${formatDate(renewal.expiration_date)}` : null}
                    </td>
                    <td>{renewal.premium_cents != null ? formatCurrency(renewal.premium_cents) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" style={{ padding: "1rem" }}>
          <h3>Open claims</h3>
          {!canViewClaims ? (
            <p className="empty-state" style={{ fontSize: "0.9rem" }}>
              Claim detail requires claims access. Counts above are still visible for your book.
            </p>
          ) : null}
          <table className="data-table">
            <thead>
              <tr>
                <th>Claim</th>
                <th>Client</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.open_claims.map((claim) => (
                <tr key={claim.id}>
                  <td>
                    {canViewClaims ? (
                      <Link to={`/claims/${claim.id}`}>{claim.external_ref ?? claim.id.slice(0, 8)}</Link>
                    ) : (
                      claim.external_ref ?? claim.id.slice(0, 8)
                    )}
                  </td>
                  <td>
                    {claim.customer_id ? (
                      <ClientLink id={claim.customer_id}>{claim.customer_name ?? "Client"}</ClientLink>
                    ) : (
                      claim.customer_name ?? "—"
                    )}
                  </td>
                  <td>{formatCurrency(claim.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ padding: "1rem" }}>
          <h3>Work items</h3>
          {data.work_items.length === 0 ? (
            <p className="empty-state">
              No open tasks.{" "}
              <Link to="/my-work">View full inbox</Link>
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {data.work_items.map((item) => (
                  <tr key={`${item.scope}-${item.id}`}>
                    <td>
                      {item.customer_id ? (
                        <Link to={`/clients/${item.customer_id}`}>{item.title}</Link>
                      ) : item.claim_id ? (
                        <Link to={`/claims/${item.claim_id}`}>{item.title}</Link>
                      ) : (
                        item.title
                      )}
                    </td>
                    <td>{item.due_at ? formatDate(item.due_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Link to="/my-work" className="btn btn-secondary" style={{ marginTop: "0.75rem" }}>
            Open My Work inbox
          </Link>
        </div>
      </div>
    </>
  );
}
